import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Table,
  Space,
  Typography,
  DatePicker,
  Select,
  Segmented,
  Divider,
  Tag,
  message,
} from 'antd';
import {
  SyncOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useCompany } from '../../contexts/CompanyContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { IncomeStatementResponse } from '../../services/accountingService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val || 0);

const MONTHS = [
  { value: 0, label: 'Enero' },
  { value: 1, label: 'Febrero' },
  { value: 2, label: 'Marzo' },
  { value: 3, label: 'Abril' },
  { value: 4, label: 'Mayo' },
  { value: 5, label: 'Junio' },
  { value: 6, label: 'Julio' },
  { value: 7, label: 'Agosto' },
  { value: 8, label: 'Septiembre' },
  { value: 9, label: 'Octubre' },
  { value: 10, label: 'Noviembre' },
  { value: 11, label: 'Diciembre' },
];

const IncomeStatementView: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading } = useSecureRequest();

  const [filterMode, setFilterMode] = useState<'month' | 'range'>('month');
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const [report, setReport] = useState<IncomeStatementResponse | null>(null);

  const getDateRange = () => {
    if (filterMode === 'month') {
      const start = dayjs().year(selectedYear).month(selectedMonth).startOf('month').format('YYYY-MM-DD');
      const end = dayjs().year(selectedYear).month(selectedMonth).endOf('month').format('YYYY-MM-DD');
      return { start, end };
    } else {
      return {
        start: customRange[0]?.format('YYYY-MM-DD') || dayjs().startOf('month').format('YYYY-MM-DD'),
        end: customRange[1]?.format('YYYY-MM-DD') || dayjs().endOf('month').format('YYYY-MM-DD'),
      };
    }
  };

  const loadReport = async () => {
    if (!company.current.id) return;
    try {
      const { start, end } = getDateRange();
      const res = await securePost('/accounting/reports/income-statement', {
        company_id: company.current.id,
        start_date: start,
        end_date: end,
      });
      const raw = res?.data?.report || res?.data || res?.report || (res?.revenues ? res : null);
      if (raw) {
        const summary = raw.summary || {};
        const sections = raw.sections || {};
        const mapAccounts = (accs: any[] = []) =>
          (Array.isArray(accs) ? accs : []).map((a: any) => ({
            ...a,
            balance: a.balance !== undefined ? a.balance : (a.net_amount !== undefined ? a.net_amount : (a.final_balance || 0)),
            code: a.code || '',
            name: a.name || ''
          }));

        const normalized: IncomeStatementResponse = {
          company_id: raw.company_id || company.current.id,
          start_date: raw.start_date || start,
          end_date: raw.end_date || end,
          revenues: {
            accounts: mapAccounts(raw.revenues?.accounts || sections.revenues),
            total: Number(raw.revenues?.total ?? summary.net_revenue ?? summary.gross_revenue ?? 0),
          },
          costs: {
            accounts: mapAccounts(raw.costs?.accounts || sections.cogs),
            total: Number(raw.costs?.total ?? summary.cogs ?? 0),
          },
          production_costs: {
            accounts: mapAccounts(raw.production_costs?.accounts || sections.production_costs),
            total: Number(raw.production_costs?.total ?? summary.production_costs ?? 0),
          },
          expenses: {
            accounts: mapAccounts(raw.expenses?.accounts || sections.expenses),
            total: Number(raw.expenses?.total ?? summary.expenses ?? 0),
          },
          gross_profit: Number(raw.gross_profit ?? summary.gross_profit ?? 0),
          gross_margin_percentage: Number(raw.gross_margin_percentage ?? raw.gross_margin_percent ?? summary.gross_margin_percent ?? 0),
          operating_income: Number(raw.operating_income ?? summary.operating_income ?? 0),
          operating_margin_percentage: Number(raw.operating_margin_percentage ?? raw.operating_margin_percent ?? summary.net_margin_percent ?? 0),
        };
        setReport(normalized);
      }
    } catch (err) {
      console.error('Error loading income statement:', err);
      message.error('No se pudo generar el Estado de Resultados.');
    }
  };

  useEffect(() => {
    if (company.current.id) {
      loadReport();
    }
  }, [company.current.id, filterMode, selectedYear, selectedMonth, customRange]);

  const accountColumns = [
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (code: string) => <Text strong>{code}</Text>,
    },
    {
      title: 'Cuenta',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Valor Acumulado',
      dataIndex: 'balance',
      key: 'balance',
      align: 'right' as const,
      width: 160,
      render: (val: number) => <Text strong>{formatCurrency(val)}</Text>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Estado de Resultados (P&L)</Title>
          <Text type="secondary">
            {report
              ? `Período comprendido desde el ${dayjs(report.start_date).format('DD/MM/YYYY')} hasta el ${dayjs(report.end_date).format('DD/MM/YYYY')}`
              : 'Desempeño financiero operacional, ingresos, costos y márgenes'}
          </Text>
        </div>
        <Space wrap>
          <Button icon={<SyncOutlined spin={loading} />} onClick={loadReport}>
            Refrescar
          </Button>
        </Space>
      </div>

      {/* Filter Bar */}
      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Período:</Text>
            <Segmented
              value={filterMode}
              onChange={(val) => setFilterMode(val as 'month' | 'range')}
              options={[
                { label: 'Por Mes / Año', value: 'month' },
                { label: 'Rango Personalizado', value: 'range' },
              ]}
            />
          </Col>

          <Col xs={24} md={12}>
            {filterMode === 'month' ? (
              <Space wrap>
                <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 110 }}>
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <Option key={y} value={y}>{y}</Option>
                  ))}
                </Select>
                <Select value={selectedMonth} onChange={setSelectedMonth} style={{ width: 140 }}>
                  {MONTHS.map((m) => (
                    <Option key={m.value} value={m.value}>{m.label}</Option>
                  ))}
                </Select>
              </Space>
            ) : (
              <RangePicker
                value={customRange}
                onChange={(d) => d && setCustomRange([d[0]!, d[1]!])}
                format="DD/MM/YYYY"
              />
            )}
          </Col>
        </Row>
      </Card>

      {/* High-Level Summary KPI Cards */}
      {report && (
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#f6ffed', borderRadius: 8 }}>
              <Statistic
                title={<span style={{ color: '#389e0d', fontWeight: 600 }}>Ingresos Operacionales (+)</span>}
                value={report.revenues?.total || 0}
                formatter={(v) => formatCurrency(Number(v))}
                prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Clase 4 (Ventas y Servicios)</Text>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff2e8', borderRadius: 8 }}>
              <Statistic
                title={<span style={{ color: '#d4380d', fontWeight: 600 }}>Costos de Venta (-)</span>}
                value={(report.costs?.total || 0) + (report.production_costs?.total || 0)}
                formatter={(v) => formatCurrency(Number(v))}
                prefix={<FallOutlined style={{ color: '#fa541c' }} />}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Clases 6 y 7</Text>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#e6f7ff', borderRadius: 8 }}>
              <Statistic
                title={<span style={{ color: '#0958d9', fontWeight: 600 }}>Utilidad Bruta (=)</span>}
                value={report.gross_profit || 0}
                formatter={(v) => formatCurrency(Number(v))}
                prefix={<RiseOutlined style={{ color: '#1677ff' }} />}
              />
              <div style={{ marginTop: 4 }}>
                <Text style={{ fontSize: 12 }}>Margen Bruto: </Text>
                <Tag color="blue">{report.gross_margin_percentage || 0}%</Tag>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              bordered={false}
              style={{
                background: (report.operating_income || 0) >= 0 ? '#f0f5ff' : '#fff1f0',
                borderRadius: 8,
              }}
            >
              <Statistic
                title={
                  <span style={{ color: (report.operating_income || 0) >= 0 ? '#1d39c4' : '#cf1322', fontWeight: 600 }}>
                    Utilidad Operacional (EBIT)
                  </span>
                }
                value={report.operating_income || 0}
                formatter={(v) => formatCurrency(Number(v))}
                valueStyle={{ color: (report.operating_income || 0) >= 0 ? '#1d39c4' : '#cf1322' }}
              />
              <div style={{ marginTop: 4 }}>
                <Text style={{ fontSize: 12 }}>Margen Operacional: </Text>
                <Tag color={(report.operating_income || 0) >= 0 ? 'geekblue' : 'red'}>
                  {report.operating_margin_percentage || 0}%
                </Tag>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Financial Statement Breakdown Structure */}
      {report && (
        <Card bordered={false} style={{ borderRadius: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* 1. Ingresos */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0, color: '#389e0d' }}>
                  1. INGRESOS DE ACTIVIDADES ORDINARIAS
                </Title>
                <Text strong style={{ fontSize: 16, color: '#389e0d' }}>
                  {formatCurrency(report.revenues?.total || 0)}
                </Text>
              </div>
              <Table
                dataSource={report.revenues?.accounts || []}
                columns={accountColumns}
                rowKey="code"
                pagination={false}
                size="small"
              />
            </div>

            <Divider style={{ margin: '8px 0' }} />

            {/* 2. Costo de Ventas */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0, color: '#d4380d' }}>
                  2. COSTO DE VENTAS Y OPERACIÓN
                </Title>
                <Text strong style={{ fontSize: 16, color: '#d4380d' }}>
                  {formatCurrency((report.costs?.total || 0) + (report.production_costs?.total || 0))}
                </Text>
              </div>
              <Table
                dataSource={[...(report.costs?.accounts || []), ...(report.production_costs?.accounts || [])]}
                columns={accountColumns}
                rowKey="code"
                pagination={false}
                size="small"
              />
            </div>

            {/* 3. Utilidad Bruta Bar */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#e6f7ff',
                padding: '12px 16px',
                borderRadius: 6,
              }}
            >
              <div>
                <Text strong style={{ fontSize: 16, color: '#0958d9' }}>UTILIDAD BRUTA</Text>
                <div><Text type="secondary" style={{ fontSize: 12 }}>Ingresos menos Costo de Ventas</Text></div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Text strong style={{ fontSize: 18, color: '#0958d9' }}>
                  {formatCurrency(report.gross_profit || 0)}
                </Text>
                <div><Tag color="blue">Margen: {report.gross_margin_percentage || 0}%</Tag></div>
              </div>
            </div>

            {/* 4. Gastos Operacionales */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0, color: '#fa8c16' }}>
                  3. GASTOS OPERACIONALES (ADMINISTRACIÓN Y VENTAS)
                </Title>
                <Text strong style={{ fontSize: 16, color: '#fa8c16' }}>
                  {formatCurrency(report.expenses?.total || 0)}
                </Text>
              </div>
              <Table
                dataSource={report.expenses?.accounts || []}
                columns={accountColumns}
                rowKey="code"
                pagination={false}
                size="small"
              />
            </div>

            {/* 5. Utilidad Operacional Bar */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: (report.operating_income || 0) >= 0 ? '#f6ffed' : '#fff1f0',
                padding: '16px 20px',
                borderRadius: 8,
                border: `1px solid ${(report.operating_income || 0) >= 0 ? '#b7eb8f' : '#ffa39e'}`,
              }}
            >
              <div>
                <Title level={4} style={{ margin: 0, color: (report.operating_income || 0) >= 0 ? '#237804' : '#cf1322' }}>
                  UTILIDAD OPERACIONAL DEL EJERCICIO (EBIT)
                </Title>
                <Text type="secondary">Resultado neto de las operaciones ordinarias antes de impuestos</Text>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Title level={3} style={{ margin: 0, color: (report.operating_income || 0) >= 0 ? '#237804' : '#cf1322' }}>
                  {formatCurrency(report.operating_income || 0)}
                </Title>
                <Tag color={(report.operating_income || 0) >= 0 ? 'green' : 'red'} style={{ marginTop: 4 }}>
                  Margen Operacional: {report.operating_margin_percentage || 0}%
                </Tag>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default IncomeStatementView;
